// Chiamate del modulo firma elettronica. Il PIN non viene mai loggato né ritentato.
import api from 'src/utils/api'

export const PROGRAM_TYPE_CONFIRMING = 'confirmingProgram'
export const EVENTS_PAGE_SIZE = 50

export const extractApiErrorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data
  if (data) {
    if (data.error && data.error.message) return data.error.message
    if (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message) {
      return data.errors[0].message
    }
    if (data.message) return data.message
  }
  return fallback
}

export const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.items)) return payload.items
  if (payload && Array.isArray(payload.events)) return payload.events
  if (payload && Array.isArray(payload.results)) return payload.results
  return []
}

// Email amministratore: il server accetta anche indirizzi senza utente.
export const normalizeAdminEmail = (value) => String(value || '').trim().toLowerCase()

const modulePath = (companyId, suffix) => `/${companyId}/electronicSignature${suffix}`

export const getElectronicSignatureModule = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/module') })

export const saveElectronicSignatureModule = (companyId, body) =>
  api.putResource('companies', { path: modulePath(companyId, '/module'), body })

export const listSigners = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/signers') })

// Il body contiene il PIN: il chiamante non deve ritentare questa richiesta.
export const createSigner = (companyId, body) =>
  api.postResource('companies', { path: modulePath(companyId, '/signers'), body })

// Solo i campi del profilo. Il PIN non passa da qui: lo inserisce il titolare su /credentials.
export const updateSigner = (companyId, profileId, body) =>
  api.putResource('companies', { path: modulePath(companyId, `/signers/${profileId}`), body })

export const setSignerCredentials = (companyId, profileId, body) =>
  api.putResource('companies', { path: modulePath(companyId, `/signers/${profileId}/credentials`), body })

export const setSignerConsent = (companyId, profileId, body) =>
  api.putResource('companies', { path: modulePath(companyId, `/signers/${profileId}/consent`), body })

export const getConsentText = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/consentText') })

export const deleteSigner = (companyId, profileId) =>
  api.deleteResource('companies', { path: modulePath(companyId, `/signers/${profileId}`) })

export const listSignableTemplates = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/templates') })

export const getTemplatePreview = (companyId, templateName) =>
  api.getResource('companies', {
    path: modulePath(companyId, `/templates/${encodeURIComponent(templateName)}/preview`)
  })

export const getTemplatePreviewPdf = (companyId, templateName) =>
  api.getResource('companies', {
    path: modulePath(companyId, `/templates/${encodeURIComponent(templateName)}/previewPdf`)
  })

export const listSignaturePrograms = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/programs') })

export const listAuthorizations = (companyId) =>
  api.getResource('companies', { path: modulePath(companyId, '/authorizations') })

export const saveAuthorization = (companyId, body) =>
  api.putResource('companies', { path: modulePath(companyId, '/authorizations'), body })

export const updateAuthorizationStatus = (companyId, authorizationId, status) =>
  api.putResource('companies', {
    path: modulePath(companyId, `/authorizations/${authorizationId}`),
    body: { status }
  })

export const deleteAuthorization = (companyId, authorizationId) =>
  api.deleteResource('companies', { path: modulePath(companyId, `/authorizations/${authorizationId}`) })

export const listAuditEvents = (companyId, { limit = EVENTS_PAGE_SIZE, skip = 0 } = {}) =>
  api.getResource('companies', {
    path: modulePath(companyId, `/auditEvents?limit=${limit}&skip=${skip}`)
  })

export const listSignatureEvents = (companyId, { limit = EVENTS_PAGE_SIZE, skip = 0 } = {}) =>
  api.getResource('companies', {
    path: modulePath(companyId, `/events?limit=${limit}&skip=${skip}`)
  })

export const getSignedDocumentUrl = (companyId, eventId) =>
  api.getResource('companies', { path: modulePath(companyId, `/events/${eventId}/signedDocument`) })

export const getAuditUrl = (companyId, eventId) =>
  api.getResource('companies', { path: modulePath(companyId, `/events/${eventId}/audit`) })

export const getAutomaticSignatureProjection = (companyId, programId) =>
  api.getResource('companies', {
    path: `/${companyId}/confirmingProgram/${programId}/factorItBper/automaticSignature`
  })

export const searchCompaniesByText = (search) =>
  api.getResource('companies', {
    path: '/all/searchByText',
    params: {
      search,
      fields: ['name', 'vatId', 'uuid', 'status'].join(',')
    }
  })

export const resolvePresignedUrl = (payload) => {
  const value = typeof payload === 'string' ? payload : payload && (payload.url || payload.href)
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return null
  return value
}
